// React Query hooks for the WB tickets surface (Phase C9).
//
// Pairs with GET /api/v1/wb/tickets (paginated, server-side filters) and
// GET /api/v1/wb/tickets/awaiting-second-weigh (non-paginated, polled).
// DTOs mirror server/src/modules/wb/tickets/types.ts (TicketSummaryDto +
// AwaitingSecondWeighDto). Group 2 ships per-ticket detail / mutations
// in `useWbTicket.ts`; this file owns the list-shaped reads only.

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Shared enums -----------------------------------------------------------

export type TicketStatus = 'draft' | 'open' | 'completed' | 'voided';

export type TicketDirection = 'inwards' | 'outwards';

export type WeighingMode =
  | 'two_pass'
  | 'stored_tare'
  | 'manual_tare'
  | 'units_only';

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

// -- DTOs -------------------------------------------------------------------

export interface TicketCustomerRef {
  readonly enc_id: string;
  readonly name: string;
}

export interface TicketYardRef {
  readonly enc_id: string;
  readonly name: string;
}

/** One previewed ticket line shown on the kiosk card grid (migration 167). */
export interface TicketLinePreview {
  readonly product: string | null;
  readonly net_kg: number | null;
  readonly total: number | null;
}

/** Active lock surfaced on a kiosk card (migration 167). `locked_by` is the
 *  numeric staff id, matched against the current staff's id for ownership. */
export interface TicketSummaryLock {
  readonly locked_by: number;
  readonly locked_by_name: string;
  readonly origin: 'kiosk' | 'main';
  readonly expires_at: string;
}

/** Full lock status returned by GET/POST /wb/tickets/:enc_id/lock (mig 167). */
export interface TicketLockDto {
  readonly locked_by: number;
  readonly locked_by_name: string;
  readonly origin: 'kiosk' | 'main';
  readonly acquired_at: string;
  readonly expires_at: string;
  readonly is_expired: boolean;
}

/** POST /wb/tickets/:enc_id/lock result. `lock` is the current holder when
 *  `acquired` is false (someone else holds a fresh lock); null on success. */
export interface AcquireLockResult {
  readonly acquired: boolean;
  readonly lock: TicketLockDto | null;
}

export interface TicketSummaryDto {
  readonly enc_id: string;
  readonly ticket_number: string | null;
  readonly direction: TicketDirection;
  readonly status: TicketStatus;
  readonly weighing_mode: WeighingMode;
  readonly customer: TicketCustomerRef;
  readonly vehicle_reg_snapshot: string | null;
  readonly net_weight_kg: number | null;
  readonly total: number;
  readonly yard: TicketYardRef;
  readonly created_at: string;
  readonly completed_at: string | null;
  // List-only enrichment (migration 167); absent on older payloads.
  readonly line_count?: number;
  readonly line_preview?: ReadonlyArray<TicketLinePreview>;
  readonly lock?: TicketSummaryLock | null;
  readonly notes_count: number;
  readonly printed_at?: string | null;
}

export interface AwaitingSecondWeighDto {
  readonly enc_id: string;
  readonly ticket_number: string | null;
  readonly direction: TicketDirection;
  readonly customer: TicketCustomerRef;
  readonly vehicle_reg_snapshot: string | null;
  readonly gross_weight_kg: number;
  readonly gross_weighed_at: string;
  readonly yard: TicketYardRef;
  readonly weighbridge_id_primary: number | null;
  readonly time_on_site_minutes: number;
}

// -- List tickets -----------------------------------------------------------

export interface ListTicketsParams {
  /** Single status OR an array of statuses (server accepts `?status=` repeated). */
  status?: TicketStatus | TicketStatus[];
  direction?: TicketDirection;
  customer_enc_id?: string;
  vehicle_enc_id?: string;
  yard_enc_id?: string;
  weighbridge_enc_id?: string;
  /** YYYY-MM-DD */
  date_from?: string;
  /** YYYY-MM-DD */
  date_to?: string;
  page?: number;
  per_page?: number;
}

interface ListTicketsResponse {
  tickets: TicketSummaryDto[];
  pagination: Pagination;
}

function ticketsQs(params: ListTicketsParams): string {
  const sp = new URLSearchParams();
  if (params.status !== undefined) {
    if (Array.isArray(params.status)) {
      for (const s of params.status) sp.append('status', s);
    } else {
      sp.set('status', params.status);
    }
  }
  if (params.direction) sp.set('direction', params.direction);
  if (params.customer_enc_id) sp.set('customer_enc_id', params.customer_enc_id);
  if (params.vehicle_enc_id) sp.set('vehicle_enc_id', params.vehicle_enc_id);
  if (params.yard_enc_id) sp.set('yard_enc_id', params.yard_enc_id);
  if (params.weighbridge_enc_id)
    sp.set('weighbridge_enc_id', params.weighbridge_enc_id);
  if (params.date_from) sp.set('date_from', params.date_from);
  if (params.date_to) sp.set('date_to', params.date_to);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined)
    sp.set('per_page', String(params.per_page));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

interface UseTicketsOpts {
  /** When true, poll every 30s (used by the Open-Tickets page). */
  enablePolling?: boolean;
}

// Server emits `{ data: rows, total, page, per_page }` from
// `success(listTicketsForActor(...))` (see `server/src/modules/wb/tickets/
// service.ts`). Older docs / test mocks shaped this as
// `{ tickets, pagination: { ... total_pages } }`. We normalise to the
// contract shape so existing consumers keep working AND the real wire
// shape works too — both reachable in the same codebase today.
function normaliseTicketsList(body: unknown): ListTicketsResponse {
  const raw = (body ?? {}) as {
    tickets?: TicketSummaryDto[];
    data?: TicketSummaryDto[];
    pagination?: Partial<Pagination>;
    total?: number;
    page?: number;
    per_page?: number;
    total_pages?: number;
  };
  const tickets = raw.tickets ?? raw.data ?? [];
  const page = raw.pagination?.page ?? raw.page ?? 1;
  const per_page = raw.pagination?.per_page ?? raw.per_page ?? tickets.length;
  const total = raw.pagination?.total ?? raw.total ?? tickets.length;
  const total_pages =
    raw.pagination?.total_pages ??
    raw.total_pages ??
    (per_page > 0 ? Math.max(1, Math.ceil(total / per_page)) : 1);
  return { tickets, pagination: { page, per_page, total, total_pages } };
}

export function useWbTickets(
  params: ListTicketsParams = {},
  opts: UseTicketsOpts = {},
) {
  const options: UseQueryOptions<ListTicketsResponse> = {
    queryKey: queryKeys.wb.tickets(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<unknown>(
        `${endpoints.wb.tickets}${ticketsQs(params)}`,
      );
      return normaliseTicketsList(res.data);
    },
    // 5s poll keeps the active-lock column (migration 167) fresh without a
    // manual refresh; staleTime sits just under it so a poll always refetches.
    staleTime: 4_000,
    refetchInterval: opts.enablePolling ? 5_000 : false,
    refetchIntervalInBackground: false,
  };
  return useQuery(options);
}

// -- Awaiting second weigh --------------------------------------------------

interface AwaitingSecondWeighResponse {
  tickets: AwaitingSecondWeighDto[];
}

export function useWbTicketsAwaitingSecond() {
  return useQuery<AwaitingSecondWeighResponse>({
    queryKey: queryKeys.wb.ticketsAwaitingSecond,
    queryFn: async () => {
      const res = await apiClient.get<AwaitingSecondWeighResponse>(
        endpoints.wb.ticketsAwaitingSecond,
      );
      return res.data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
